import { Globe } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { SUPPORTED_LOCALES, LOCALE_CONFIGS, type Locale } from '@/i18n/types';
import { FlagIcon } from '@/components/FlagIcon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface LanguageSelectorProps {
  variant?: 'navbar' | 'footer' | 'compact';
  className?: string;
}

const LanguageSelector = ({ variant = 'navbar', className = '' }: LanguageSelectorProps) => {
  const { locale, setLocale, config } = useI18n();

  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger className={`flex items-center gap-1.5 text-sm font-medium transition-all duration-300 ${className}`}>
          <FlagIcon code={locale} className="w-5 h-3.5" />
          <span className="uppercase text-[10px] font-bold tracking-wider">{locale}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[180px] bg-white/95 backdrop-blur-2xl border-black/[0.08] shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
          {SUPPORTED_LOCALES.map((loc) => {
            const locConfig = LOCALE_CONFIGS[loc];
            return (
              <DropdownMenuItem
                key={loc}
                onClick={() => setLocale(loc)}
                className={`flex items-center gap-2.5 cursor-pointer transition-colors ${locale === loc ? 'bg-punto/10 text-punto font-semibold' : 'text-black/70 hover:text-black hover:bg-black/[0.04]'}`}
              >
                <FlagIcon code={loc} className="w-5 h-3.5" />
                <span className="text-sm">{locConfig.nativeName}</span>
                {locale === loc && <span className="ml-auto text-punto text-xs">✓</span>}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${className}`}>
        <Globe className="w-4 h-4" />
        <FlagIcon code={locale} className="w-5 h-3.5" />
        <span className="hidden sm:inline">{config.nativeName}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px] bg-white/95 backdrop-blur-2xl border-black/[0.08] shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
        {SUPPORTED_LOCALES.map((loc) => {
          const locConfig = LOCALE_CONFIGS[loc];
          return (
            <DropdownMenuItem
              key={loc}
              onClick={() => setLocale(loc)}
              className={`flex items-center gap-3 cursor-pointer transition-colors ${locale === loc ? 'bg-punto/10 text-punto font-semibold' : 'text-black/70 hover:text-black hover:bg-black/[0.04]'}`}
            >
              <FlagIcon code={loc} className="w-6 h-4" />
              <div className="flex flex-col">
                <span className="font-medium">{locConfig.nativeName}</span>
                <span className="text-[10px] text-black/30">{locConfig.name}</span>
              </div>
              {locale === loc && <span className="ml-auto text-punto text-xs">✓</span>}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
